// 앱 전체 상태 관리 Context
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { BossInfo, BossSkill, BossEffect, TriggerDefinitionV2, CopyModalState, RaidEntity } from '../types';

// 편집 가능한 아이템 유니온 타입
type EditableItem = BossInfo | BossSkill | BossEffect | TriggerDefinitionV2 | null;

// 앱 상태 타입 정의
export interface AppState {
  // 현재 선택된 보스/자원
  selectedBoss: BossInfo | null;
  selectedEntity: RaidEntity | null;
  selectedSkill: BossSkill | null;
  selectedEffect: BossEffect | null;
  selectedTrigger: TriggerDefinitionV2 | null;
  
  // 사이드바 상태
  sidebarCollapsed: boolean;
  
  // 메인 콘텐츠 상태
  activeTab: 'basic' | 'skills' | 'effects' | 'triggers' | 'triggers-v3' | 'timeline' | 'relations' | 'test' | 'motion-effects' | 'raid-management' | 'drop-item-management';
  
  // 모달 상태들
  showBossModal: boolean;
  showSkillModal: boolean;
  showEffectModal: boolean;
  showTriggerModal: boolean;
  showCopyModal: boolean;
  copyModalState: CopyModalState;
  
  // 편집 모드
  editingItem: EditableItem;
  editingMode: 'create' | 'edit' | null;
  
  // 로딩 상태
  loading: boolean;
  
  // 공통 자원 보기 모드
  viewingCommon: boolean;
  commonResourceType: 'skills' | 'effects' | null;
}

// 액션 타입 정의
type AppAction =
  | { type: 'SET_SELECTED_BOSS'; payload: BossInfo | null }
  | { type: 'SET_SELECTED_ENTITY'; payload: RaidEntity | null }
  | { type: 'SET_SELECTED_SKILL'; payload: BossSkill | null }
  | { type: 'SET_SELECTED_EFFECT'; payload: BossEffect | null }
  | { type: 'SET_SELECTED_TRIGGER'; payload: TriggerDefinitionV2 | null }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_ACTIVE_TAB'; payload: AppState['activeTab'] }
  | { type: 'SHOW_BOSS_MODAL'; payload: { editingItem?: BossInfo } }
  | { type: 'SHOW_SKILL_MODAL'; payload: { editingItem?: BossSkill } }
  | { type: 'SHOW_EFFECT_MODAL'; payload: { editingItem?: BossEffect } }
  | { type: 'SHOW_TRIGGER_MODAL'; payload: { editingItem?: TriggerDefinitionV2 } }
  | { type: 'SHOW_COPY_MODAL'; payload: CopyModalState }
  | { type: 'HIDE_ALL_MODALS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_VIEWING_COMMON'; payload: { viewing: boolean; resourceType?: 'skills' | 'effects' | null } }
  | { type: 'RESET_STATE' };

// 초기 상태
export const initialState: AppState = {
  selectedBoss: null,
  selectedEntity: null,
  selectedSkill: null,
  selectedEffect: null,
  selectedTrigger: null,
  sidebarCollapsed: false,
  activeTab: 'basic',
  showBossModal: false,
  showSkillModal: false,
  showEffectModal: false,
  showTriggerModal: false,
  showCopyModal: false,
  copyModalState: {
    isOpen: false,
    itemType: 'skill',
    sourceItem: null,
    itemName: '',
  },
  editingItem: null,
  editingMode: null,
  loading: false,
  viewingCommon: false,
  commonResourceType: null,
};

// 리듀서 함수
export const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_SELECTED_BOSS':
      return {
        ...state,
        selectedBoss: action.payload,
        selectedEntity: null, // 보스 선택시 엔티티 선택 해제
        viewingCommon: false, // 보스 선택시 공통 자원 모드 해제
        selectedSkill: null,
        selectedEffect: null,
        selectedTrigger: null,
      };
      
    case 'SET_SELECTED_ENTITY':
      console.log('SET_SELECTED_ENTITY 액션:', action.payload);
      return {
        ...state,
        selectedEntity: action.payload,
        selectedBoss: null, // 엔티티 선택시 보스 선택 해제
        viewingCommon: false,
        selectedSkill: null,
        selectedEffect: null,
        selectedTrigger: null,
      };

    case 'SET_SELECTED_SKILL':
      return {
        ...state,
        selectedSkill: action.payload,
      };

    case 'SET_SELECTED_EFFECT':
      return {
        ...state,
        selectedEffect: action.payload,
      };

    case 'SET_SELECTED_TRIGGER':
      return {
        ...state,
        selectedTrigger: action.payload,
      };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed,
      };

    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload,
        viewingCommon: state.viewingCommon && ['skills','effects'].includes(action.payload),
        commonResourceType: state.viewingCommon && ['skills','effects'].includes(action.payload) ? action.payload as 'skills'|'effects' : null,
      };

    case 'SHOW_BOSS_MODAL':
      return {
        ...state,
        showBossModal: true,
        editingItem: action.payload.editingItem || null,
        editingMode: action.payload.editingItem ? 'edit' : 'create',
      };

    case 'SHOW_SKILL_MODAL':
      return {
        ...state,
        showSkillModal: true,
        editingItem: action.payload.editingItem || null,
        editingMode: action.payload.editingItem ? 'edit' : 'create',
      };

    case 'SHOW_EFFECT_MODAL':
      console.log('🔄 AppContext SHOW_EFFECT_MODAL reducer:', action.payload);
      return {
        ...state,
        showEffectModal: true,
        editingItem: action.payload.editingItem || null,
        editingMode: action.payload.editingItem ? 'edit' : 'create',
      };

    case 'SHOW_TRIGGER_MODAL':
      return {
        ...state,
        showTriggerModal: true,
        editingItem: action.payload.editingItem || null,
        editingMode: action.payload.editingItem ? 'edit' : 'create',
      };

    case 'SHOW_COPY_MODAL':
      return {
        ...state,
        showCopyModal: true,
        copyModalState: action.payload,
      };

    case 'HIDE_ALL_MODALS':
      return {
        ...state,
        showBossModal: false,
        showSkillModal: false,
        showEffectModal: false,
        showTriggerModal: false,
        showCopyModal: false,
        copyModalState: initialState.copyModalState,
        editingItem: null,
        editingMode: null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_VIEWING_COMMON':
      return {
        ...state,
        viewingCommon: action.payload.viewing,
        commonResourceType: action.payload.resourceType || null,
        activeTab: action.payload.viewing ? action.payload.resourceType || 'skills' : state.activeTab,
        selectedEntity: action.payload.viewing ? null : state.selectedEntity,
        selectedBoss: action.payload.viewing ? null : state.selectedBoss, // 공통 자원 모드시 보스 선택 해제
      };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
};

// Context 타입 정의
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  
  // 편의 함수들
  selectBoss: (boss: BossInfo | null) => void;
  selectEntity: (entity: RaidEntity | null) => void;
  selectSkill: (skill: BossSkill | null) => void;
  selectEffect: (effect: BossEffect | null) => void;
  selectTrigger: (trigger: TriggerDefinitionV2 | null) => void;
  
  setActiveTab: (tab: AppState['activeTab']) => void;
  
  openBossModal: (editingItem?: BossInfo) => void;
  openSkillModal: (editingItem?: BossSkill) => void;
  openEffectModal: (editingItem?: BossEffect) => void;
  openTriggerModal: (editingItem?: TriggerDefinitionV2) => void;
  openCopyModal: (itemType: 'skill' | 'effect' | 'trigger', sourceItem: BossSkill | BossEffect | TriggerDefinitionV2, itemName: string) => void;
  closeAllModals: () => void;
  
  setLoading: (loading: boolean) => void;
  
  viewCommonResources: (resourceType: 'skills' | 'effects') => void;
  exitCommonView: () => void;
}

// Context 생성
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider 컴포넌트
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // 편의 함수들
  const selectBoss = (boss: BossInfo | null) => {
    dispatch({ type: 'SET_SELECTED_BOSS', payload: boss });
  };
  
  const selectEntity = (entity: RaidEntity | null) => {
    console.log('AppContext selectEntity 호출됨:', entity);
    dispatch({ type: 'SET_SELECTED_ENTITY', payload: entity });
  };

  const selectSkill = (skill: BossSkill | null) => {
    dispatch({ type: 'SET_SELECTED_SKILL', payload: skill });
  };

  const selectEffect = (effect: BossEffect | null) => {
    dispatch({ type: 'SET_SELECTED_EFFECT', payload: effect });
  };

  const selectTrigger = (trigger: TriggerDefinitionV2 | null) => {
    dispatch({ type: 'SET_SELECTED_TRIGGER', payload: trigger });
  };

  const setActiveTab = (tab: AppState['activeTab']) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  };

  const openBossModal = (editingItem?: BossInfo) => {
    dispatch({ type: 'SHOW_BOSS_MODAL', payload: { editingItem } });
  };

  const openSkillModal = (editingItem?: BossSkill) => {
    dispatch({ type: 'SHOW_SKILL_MODAL', payload: { editingItem } });
  };

  const openEffectModal = (editingItem?: BossEffect) => {
    console.log('📝 AppContext openEffectModal 호출:', { editingItem });
    dispatch({ type: 'SHOW_EFFECT_MODAL', payload: { editingItem } });
  };

  const openTriggerModal = (editingItem?: TriggerDefinitionV2) => {
    dispatch({ type: 'SHOW_TRIGGER_MODAL', payload: { editingItem } });
  };

  const openCopyModal = (itemType: 'skill' | 'effect' | 'trigger', sourceItem: BossSkill | BossEffect | TriggerDefinitionV2, itemName: string) => {
    dispatch({
      type: 'SHOW_COPY_MODAL',
      payload: {
        isOpen: true,
        itemType,
        sourceItem,
        itemName,
      },
    });
  };

  const closeAllModals = () => {
    dispatch({ type: 'HIDE_ALL_MODALS' });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const viewCommonResources = (resourceType: 'skills' | 'effects') => {
    dispatch({
      type: 'SET_VIEWING_COMMON',
      payload: { viewing: true, resourceType },
    });
  };

  const exitCommonView = () => {
    dispatch({
      type: 'SET_VIEWING_COMMON',
      payload: { viewing: false },
    });
  };

  const contextValue: AppContextType = {
    state,
    dispatch,
    selectBoss,
    selectEntity,
    selectSkill,
    selectEffect,
    selectTrigger,
    setActiveTab,
    openBossModal,
    openSkillModal,
    openEffectModal,
    openTriggerModal,
    openCopyModal,
    closeAllModals,
    setLoading,
    viewCommonResources,
    exitCommonView,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;